import React, { useMemo } from 'react';
import { AutoMovement } from '../../../../types';
import DataTable, { Column } from '../../../../components/shared/DataTable';
import ActionMenu, { createEditAction, createDeleteAction, createDuplicateAction } from '../../../../components/shared/ActionMenu';
import { AppIcon } from '../../../../components/icons/AppIcon';

interface AutoMovementTableProps {
    movements: AutoMovement[];
    onEdit: (movement: AutoMovement) => void;
    onDelete: (movement: AutoMovement) => void;
    onDuplicate: (movement: AutoMovement) => void;
    selectedIds?: Set<string>;
    onToggleSelection?: (id: string) => void;
    onToggleAll?: () => void;
}

const modeLabels: Record<AutoMovement['mode'], string> = {
    contains: 'Contém',
    starts_with: 'Começa com',
    exact: 'Exato'
};

/** Formata número de WhatsApp para exibição: +55 11 99999-9999 */
function formatChannelName(channelName: string | undefined): string {
    if (!channelName) return '-';

    // Se for apenas dígitos (número de telefone bruto), formata
    const digits = channelName.replace(/\D/g, '');
    if (digits.length >= 10) {
        // Formato: +55 11 99999-9999
        const country = digits.slice(0, 2);
        const ddd = digits.slice(2, 4);
        const rest = digits.slice(4);
        const formatted = rest.length === 9
            ? `${rest.slice(0, 5)}-${rest.slice(5)}`
            : `${rest.slice(0, 4)}-${rest.slice(4)}`;
        return `+${country} ${ddd} ${formatted}`;
    }

    return channelName;
}

const AutoMovementTable: React.FC<AutoMovementTableProps> = ({
    movements, onEdit, onDelete, onDuplicate,
    selectedIds = new Set(), onToggleSelection, onToggleAll
}) => {
    const columns: Column<AutoMovement>[] = useMemo(() => [
        {
            key: 'message',
            header: 'Mensagem',
            render: (m) => <span className="font-medium text-foreground">{m.message}</span>
        },
        {
            key: 'mode',
            header: 'Modo',
            render: (m) => (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-subtle text-primary">
                    {modeLabels[m.mode]}
                </span>
            )
        },
        {
            key: 'channel',
            header: 'Canal',
            render: (m) => (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <AppIcon name="whatsapp" className="w-4 h-4 text-accent shrink-0" />
                    <span>{formatChannelName(m.channelName)}</span>
                </div>
            )
        },
        {
            key: 'stage',
            header: 'Etapa',
            render: (m) => <span className="text-sm text-muted-foreground">{m.stageName || '-'}</span>
        },
        {
            key: 'actions',
            header: 'Ações',
            align: 'right',
            render: (m) => (
                <ActionMenu actions={[
                    createEditAction(() => onEdit(m)),
                    createDuplicateAction(() => onDuplicate(m)),
                    createDeleteAction(() => onDelete(m))
                ]} />
            )
        }
    ], [onEdit, onDuplicate, onDelete]);

    return (
        <DataTable
            columns={columns}
            data={movements}
            getRowKey={(m) => m.id}
            emptyMessage="Nenhuma movimentação automática encontrada."
            selectable
            selectedIds={selectedIds}
            onToggleSelection={onToggleSelection}
            onToggleAll={onToggleAll}
        />
    );
};

export default AutoMovementTable;
